import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { schoolRegistrationFormSchema } from '../components/registrationFormComponents/schema';

import { AppContext } from '../contexts/app.context';
import { Button, Input, Loader, Text } from '../components';
import {
  EstablishmentDetailsForm,
  ClassDetailsForm,
  EmailConsentForm,
  InchargePersonForm,
  TotalStudentDisplay,
} from '../components/registrationFormComponents';
import { schoolRegistration, updateDashboardDetails, updateRegistrationDetails } from '../service';
import { toast } from 'react-toastify';
import { scrollToClass, submitHelper } from '../components/registrationFormComponents/util';
import {
  FINAL_BOOKING_TIME,
  FINAL_EDIT_TIME,
  NUMBER_OF_TOUR_STUDENTS,
} from '../components/registrationFormComponents/data';

const translationPrefix = 'schoolRegistrationForm.form';

const classInitialValues = {
  name: '',
  level: '',
  otherLevel: '',
  numberOfStudents: '',
  sector: '',
  visitDay: '',
  arrivalTime: '',
  departureTime: '',
  modeOfTransport: '',
  otherModeOfTransport: '',
  picnicOnSite: 'false',
  picnicTimeSlot: '',
  handicap: 'false',
  handicapDescription: '',
  responsibleInformation: {
    firstName: '',
    lastName: '',
    function: '',
    email: '',
    phone: '',
  },
  bookExperience: 'true',
  experienceDetails: {
    tourType: 'introduction',
    timeSlot: '',
    level: '',
  },
};

const SchoolRegistrationForm = ({ storedData, refresh, refreshing }) => {
  const { t } = useTranslation();
  const { admin } = useContext(AppContext);
  const { setBaseContainerConfig } = useContext(AppContext);

  const { id } = useParams();

  const [saving, setSaving] = useState(false);
  const [openClassIndex, setOpenClassIndex] = useState(null);
  const [experienceInteractionConfig, setExperienceInteractionConfig] = useState({
    visible: true,
    editable: true,
    newBooking: true,
    finalBookingTimePassed: false,
    slotEditTimePassed: false,
  });

  const formik = useFormik({
    initialValues: {
      establishmentDetails: {
        privateContract: 'false',
        rep: 'false',
      },
      inchargePersonDetails: {},
      inchargePersonAnother: 'true',
      inchargePersonAnotherDetails: {},
      classes: [
        {
          ...classInitialValues,
          bookExperience: 'true',
        },
      ],
      emailConsent: {
        emailConsent: 'true',
        studentsSafetyConsent: 'false',
        email: '',
      },
    },
    validationSchema: schoolRegistrationFormSchema,
    onSubmit: async (values) => {
      try {
        if (!storedData && experienceInteractionConfig.finalBookingTimePassed) {
          toast.error(t(`${translationPrefix}.popupError.bookingTimePassed`));
          return;
        }

        const emailConsentGiven = values?.emailConsent?.emailConsent;
        const emailConsentEmail = values?.emailConsent?.email;
        const inchargePersonEmail = values?.inchargePersonDetails?.email;

        if (emailConsentGiven === 'true' && emailConsentEmail !== inchargePersonEmail) {
          toast.error(t('schoolRegistrationForm.popupError.emailConsentEmail'));
          return;
        }

        let classStudentNumberErrors = [];

        const studentsLimit = NUMBER_OF_TOUR_STUDENTS.introduction;

        values?.classes?.forEach((classDetail) => {
          if (classDetail?.visitDay === 'thu' || classDetail?.visitDay === 'fri') {
            if (Number(classDetail?.numberOfStudents) < studentsLimit.MIN) {
              classStudentNumberErrors.push(
                `${classDetail?.name} ${t('schoolRegistrationForm.popupError.numberOfStudentsMin')}`,
              );
            }
            if (Number(classDetail?.numberOfStudents) > studentsLimit.MAX) {
              classStudentNumberErrors.push(
                `${classDetail?.name} ${t('schoolRegistrationForm.popupError.numberOfStudentsMax')}`,
              );
            }
          }
        });

        if (classStudentNumberErrors.length > 0) {
          classStudentNumberErrors.forEach((error) => {
            toast.error(error);
          });
          return;
        }

        const payload = {
          ...values,
          classes: values.classes.map((classDetail) => ({
            ...classDetail,
            bookExperience: 'true',
            experienceDetails: {
              tourType: 'introduction',
              timeSlot: classDetail.arrivalTime,
              level: classDetail.level,
            },
          })),
        };

        setSaving(true);
        !storedData
          ? await schoolRegistration(payload)
          : id && admin
            ? await updateRegistrationDetails(id, { ...payload, formType: 'school' })
            : await updateDashboardDetails({ ...payload, formType: 'school' });
        toast.success(t('schoolRegistrationForm.success'));

        if (refresh) {
          refresh();
        }
      } catch (error) {
        console.log(error);
        if (error?.response?.data?.errors) {
          const errors = Object.keys(error?.response?.data?.errors);
          errors.forEach((error) => {
            toast.error(t(`${translationPrefix}.popupError.${error}`));
          });
        } else {
          toast.error(t('schoolRegistrationForm.error'));
        }
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!storedData) return;

    formik.setValues(storedData);
  }, [storedData]);

  useEffect(() => {
    const finalBookingTimePassed = new Date() >= FINAL_BOOKING_TIME;

    setExperienceInteractionConfig((prev) => ({
      ...(finalBookingTimePassed
        ? {
            visible: storedData?.classes?.length > 0,
            editable: admin?.specialPermissions?.includes('edit'),
            newBooking: storedData ? Object.keys(storedData)?.length === 0 : true,
            finalBookingTimePassed,
          }
        : prev),
      slotEditTimePassed: !(id && admin) && new Date() >= FINAL_EDIT_TIME,
    }));
  }, [storedData, admin, id]);

  useEffect(() => {
    setBaseContainerConfig({
      heading: t('schoolRegistrationForm.header.heading'),
      subHeading: t('schoolRegistrationForm.header.subHeading'),
      buttonText: t('loginButtonText'),
      buttonURL: '/login',
    });
  }, []);

  return (
    <>
      <div className="text-left">
        <Text type={'big'} text={t('schoolRegistrationForm.heading')} />
      </div>

      <form
        onSubmit={(e) => {
          submitHelper(e, formik, t);
        }}>
        <div className="w-full mt-12">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.heading`)}
            className={
              'bg-teal-20 text-center font-poppins font-bold text-white p-2 w-full rounded-md mb-2'
            }
          />

          <div className="bg-teal-10 rounded-lg p-4 sm:px-10 pt-10">
            <EstablishmentDetailsForm formik={formik} translationPrefix={translationPrefix} />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePerson.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <InchargePersonForm formik={formik} translationPrefix={translationPrefix} />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePersonAnother.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <Input
              name={'inchargePersonAnother'}
              id={'inchargePersonAnother'}
              paragraphLabel={t(`${translationPrefix}.inchargePersonAnother.label`)}
              paragraphLabelClassName="text-center"
              radioClassName="justify-center"
              type={'radio'}
              options={[
                {
                  value: 'false',
                  label: t(`${translationPrefix}.inchargePersonAnother.false`),
                },
                {
                  value: 'true',
                  label: t(`${translationPrefix}.inchargePersonAnother.true`),
                },
              ]}
              defaultValue={formik.values?.inchargePersonAnother || 'true'}
              onChange={(value) => {
                formik.setFieldValue('inchargePersonAnother', value);
              }}
            />

            {formik.values?.inchargePersonAnother === 'false' && (
              <>
                <span className="block my-10 w-full" />
                <InchargePersonForm
                  formik={formik}
                  objectName={'inchargePersonAnotherDetails'}
                  translationPrefix={translationPrefix}
                />
              </>
            )}
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.visit.heading`)}
            className={
              'bg-yellow-10 text-center font-poppins font-bold text-white p-2 w-full rounded-md mb-2'
            }
          />

          <div className="bg-teal-10 rounded-lg p-4 sm:px-10 pt-10">
            <ClassDetailsForm
              formik={formik}
              translationPrefix={translationPrefix}
              openClassIndex={openClassIndex}
              experienceInteractionConfig={experienceInteractionConfig}
            />

            <div className="w-full flex items-center justify-center my-8">
              <Button
                type="button"
                text={t('schoolRegistrationForm.addClassButtonText')}
                className={'bg-transparent border-2 border-navy-10 !text-navy-10 rounded-md'}
                disabled={
                  experienceInteractionConfig.finalBookingTimePassed &&
                  !experienceInteractionConfig.editable
                }
                callback={() => {
                  formik.setFieldValue('classes', [
                    ...formik.values.classes,
                    {
                      ...classInitialValues,
                      bookExperience: 'true',
                    },
                  ]);
                  setOpenClassIndex(formik.values.classes.length);
                  scrollToClass(`class-${formik.values.classes.length}`);
                }}
              />
            </div>
            <TotalStudentDisplay formik={formik} translationPrefix={translationPrefix} />
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'medium'}
            text={t(`${translationPrefix}.emailConsent.heading`)}
            className={'mb-6 mt-4'}
          />

          <EmailConsentForm formik={formik} translationPrefix={translationPrefix} />

          <Button
            type="submit"
            text={t('schoolRegistrationForm.submitButtonText')}
            className={'mt-20'}
            loading={formik.isSubmitting}
            disabled={!storedData && experienceInteractionConfig.finalBookingTimePassed}
          />

          <div className="bg-teal-10 rounded-lg p-6 mt-12">
            <Text
              type={'medium'}
              text={t(`${translationPrefix}.support.heading`)}
              className={'text-navy-10'}
            />
            <Text
              type={'small'}
              text={t(`${translationPrefix}.support.paragraph`)}
              className={'mb-2'}
            />
          </div>
          <Text
            type={'small'}
            text={t(`${translationPrefix}.termsAndConditions`)}
            className={'mt-6 text-navy-10 underline'}
          />
        </div>
      </form>

      {(saving || refreshing) && (
        <Loader
          bgColor={'transparent'}
          loaderType={'sync'}
          text={storedData ? t('updating') : t('saving')}
        />
      )}
    </>
  );
};

export default SchoolRegistrationForm;
